import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  BriefcaseBusiness,
  Check,
  Sparkles,
  Target,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  OnboardingUserMenu,
  type OnboardingUserMenuUser,
} from "./OnboardingUserMenu";

export interface OnboardingShellStep {
  key: string;
  label: string;
}

interface OnboardingShellProps {
  steps: OnboardingShellStep[];
  /** Index into `steps`; ignored when `finished`. */
  currentIndex: number;
  /** Step counter, e.g. "Steg 2 av 5" — shown in the mobile header. */
  overline: string;
  title: string;
  children: ReactNode;
  /** All steps done — progress renders fully completed. */
  finished?: boolean;
  /** When set, an account avatar + menu is shown top-right. */
  user?: OnboardingUserMenuUser;
  /** Sign out handler for the account menu. Required when `user` is set. */
  onSignOut?: () => void;
}

function favicon(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
}

/* ── Left-panel showcases ─────────────────────────────────────────────── */

function QuoteShowcase() {
  const { t } = useTranslation("onboarding");
  return (
    <blockquote className="mx-auto max-w-md px-8 text-center">
      <div className="mx-auto mb-6 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
        <Sparkles className="h-5 w-5 text-primary" />
      </div>
      <p className="text-xl leading-relaxed xl:text-[1.35rem]">
        <span className="font-medium text-foreground">
          {t("shell.quote.lead")}
        </span>{" "}
        <span className="text-muted-foreground">{t("shell.quote.rest")}</span>
      </p>
    </blockquote>
  );
}

/** Skeleton bar for the mock profile card. */
function Bar({ className }: { className?: string }) {
  return <div className={cn("h-2 rounded-full bg-foreground/[0.08]", className)} />;
}

function ProfileShowcase() {
  return (
    <div className="w-72 space-y-4 rounded-xl border border-border bg-background p-6 shadow-sm">
      <div className="flex items-center gap-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
        <Bar className="w-28 bg-foreground/[0.14]" />
      </div>
      <div className="space-y-2.5 pt-1">
        <Bar className="w-full" />
        <Bar className="w-5/6" />
        <Bar className="w-4/6" />
      </div>
      <div className="flex gap-2 pt-1">
        <div className="rounded-full border border-border px-3 py-1 text-[10px] font-medium text-muted-foreground">
          B2B
        </div>
        <div className="rounded-full border border-border px-3 py-1 text-[10px] font-medium text-muted-foreground">
          SaaS
        </div>
      </div>
    </div>
  );
}

const ICON_CLOUD = [
  { domain: "hubspot.com", className: "left-[8%] top-[22%]" },
  { domain: "calendar.google.com", className: "left-[44%] top-[6%]" },
  { domain: "slack.com", className: "left-[78%] top-[26%]" },
  { domain: "gmail.com", className: "left-[22%] top-[58%]" },
  { domain: "salesforce.com", className: "left-[56%] top-[44%]" },
  { domain: "outlook.com", className: "left-[70%] top-[72%]" },
] as const;

function IntegrationsShowcase() {
  return (
    <div className="relative h-72 w-96">
      {ICON_CLOUD.map((icon) => (
        <div
          key={icon.domain}
          className={cn(
            "absolute flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-background shadow-sm",
            icon.className,
          )}
        >
          <img src={favicon(icon.domain)} alt="" className="h-7 w-7" />
        </div>
      ))}
    </div>
  );
}

function ProductsShowcase() {
  const { t } = useTranslation("onboarding");
  const rows = [
    { icon: BriefcaseBusiness, label: t("products.recruit.title") },
    { icon: Target, label: t("products.leads.title") },
    { icon: Sparkles, label: t("products.intelligence.title") },
  ];
  return (
    <div className="w-72 space-y-2.5">
      {rows.map(({ icon: Icon, label }) => (
        <div
          key={label}
          className="flex items-center gap-3 rounded-xl border border-border bg-background p-3.5 shadow-sm"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Icon className="h-4 w-4" />
          </div>
          <span className="flex-1 text-sm font-medium text-foreground">
            {label}
          </span>
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="h-3 w-3" strokeWidth={3} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ShowcasePanel({ stepKey }: { stepKey: string }) {
  return (
    <div className="relative flex h-full items-center justify-center overflow-hidden bg-muted">
      {/* Brand bloom — a quiet wash of Graftus purple behind the showcase. */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(ellipse 55% 45% at 50% 42%, hsl(var(--primary) / 0.08), transparent 70%), radial-gradient(ellipse 40% 35% at 82% 88%, hsl(var(--primary) / 0.05), transparent 65%)",
        }}
      />
      {/* Dotted grid, faded toward the edges. */}
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(circle, hsl(var(--foreground) / 0.13) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
          maskImage:
            "radial-gradient(ellipse 60% 55% at 50% 50%, black, transparent)",
        }}
      />
      <div className="relative z-10">
        {(stepKey === "set-name" || stepKey === "create-business") && (
          <QuoteShowcase />
        )}
        {stepKey === "business-profile" && <ProfileShowcase />}
        {stepKey === "integrations" && <IntegrationsShowcase />}
        {(stepKey === "select-products" || stepKey === "__done__") && (
          <ProductsShowcase />
        )}
      </div>
    </div>
  );
}

/* ── Shell ────────────────────────────────────────────────────────────── */

/**
 * Signup onboarding chrome, Midday-style: one consistent light showcase
 * panel on the left (editorial quote during account creation, product
 * showcases during configuration — never a light/dark flip mid-flow), and a
 * sparse content column on the right with a segmented progress bar and
 * serif headings.
 */
export default function OnboardingShell({
  steps,
  currentIndex,
  overline,
  title,
  children,
  finished = false,
  user,
  onSignOut,
}: OnboardingShellProps) {
  const stepKey = finished ? "__done__" : (steps[currentIndex]?.key ?? "");
  const completed = finished ? steps.length : currentIndex;
  const showUserMenu = !!user && !!onSignOut;

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="grid min-h-dvh lg:grid-cols-2">
        {/* ── Showcase panel ── */}
        <aside className="relative hidden lg:block">
          <ShowcasePanel stepKey={stepKey} />
          <img
            src="/graftus-logo.svg"
            alt="Graftus"
            className="absolute left-8 top-7 z-20 h-6 w-auto"
          />
        </aside>

        {/* ── Content column ── */}
        <main className="relative flex min-h-dvh flex-col overflow-y-auto">
          {/* Desktop account menu — pinned top-right so sign-out stays
              reachable while a long step's content scrolls. */}
          {showUserMenu && (
            <div className="fixed right-6 top-6 z-30 hidden lg:block">
              <OnboardingUserMenu user={user} onSignOut={onSignOut} />
            </div>
          )}

          {/* Mobile header */}
          <div className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur lg:hidden">
            <div className="flex items-center justify-between px-6 py-4">
              <img
                src="/graftus-logo.svg"
                alt="Graftus"
                className="h-5 w-auto dark:brightness-0 dark:invert"
              />
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium tabular-nums text-muted-foreground">
                  {overline}
                </span>
                {showUserMenu && (
                  <OnboardingUserMenu user={user} onSignOut={onSignOut} />
                )}
              </div>
            </div>
          </div>

          {/* Segmented progress — top center, Midday-style dashes. */}
          <div
            className="flex justify-center gap-1.5 pt-8 lg:pt-10"
            role="progressbar"
            aria-valuenow={completed}
            aria-valuemin={0}
            aria-valuemax={steps.length}
            aria-label={overline}
          >
            {steps.map((s, i) => (
              <div
                key={s.key}
                className={cn(
                  "h-[3px] w-10 rounded-full transition-colors duration-500 sm:w-14",
                  i < completed || (!finished && i === currentIndex)
                    ? "bg-primary"
                    : "bg-border",
                )}
              />
            ))}
          </div>

          <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-12 xl:px-20">
            <div
              key={stepKey}
              className="w-full max-w-md animate-in fade-in slide-in-from-bottom-2 duration-300"
            >
              <h1 className="font-display text-[1.7rem] leading-tight tracking-tight text-foreground sm:text-3xl">
                {title}
              </h1>
              <div className="mt-6">{children}</div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
